import type { CalendarEvent } from '@/lib/absences';
import { cn } from '@/lib/utils';
import { eachDayOfInterval, endOfMonth, getDate, isToday, startOfMonth } from 'date-fns';
import { useMemo, useState } from 'react';
import { ScheduleRow } from './schedule-row';

interface EmployeeWithEvents {
  id: string;
  name: string;
  avatar: string | null;
  position: string | null;
  events: CalendarEvent[];
}

interface ScheduleGridProps {
  employees: EmployeeWithEvents[];
  currentDate: Date;
  searchQuery: string;
}

export function ScheduleGrid({ employees, currentDate, searchQuery }: ScheduleGridProps) {
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  const days = useMemo(
    () => eachDayOfInterval({ start: startOfMonth(currentDate), end: endOfMonth(currentDate) }),
    [currentDate]
  );

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="p-4">
      <div
        className="overflow-auto rounded-lg border border-border"
        style={{ maxHeight: 'calc(100vh - 210px)' }}
      >
        <div
          className="grid"
          style={{
            gridTemplateColumns: `220px repeat(${days.length}, minmax(36px, 1fr))`,
            minWidth: `${220 + days.length * 36}px`,
          }}
        >
          {/* Header row */}
          <div className="sticky left-0 top-0 z-20 flex items-center border-b border-border bg-muted px-4 py-1.5 text-[11px] font-semibold tracking-wide text-muted-foreground">
            EMPLOYEE
          </div>
          {days.map((day, i) => {
            const today = isToday(day);
            return (
              <div
                key={i}
                className="sticky top-0 z-10 flex flex-col items-center gap-0.5 border-b border-border bg-muted py-1.5"
              >
                <span className="text-[9px] text-muted-foreground/60">
                  {dayNames[day.getDay()]}
                </span>
                <span
                  className={cn(
                    'flex h-[22px] w-[22px] items-center justify-center rounded-full text-xs font-medium text-muted-foreground',
                    today && 'bg-emerald-500 text-white'
                  )}
                >
                  {getDate(day)}
                </span>
              </div>
            );
          })}

          {/* Employee rows */}
          {employees.map((emp) => (
            <ScheduleRow
              key={emp.id}
              employee={emp}
              events={emp.events}
              days={days}
              searchQuery={searchQuery}
              onHoverDay={setHoveredDay}
              hoveredDay={hoveredDay}
            />
          ))}

          {/* No results */}
          {employees.length === 0 && (
            <div
              className="flex items-center justify-center py-12 text-sm text-muted-foreground"
              style={{ gridColumn: '1 / -1' }}
            >
              No employees match your search
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
